import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import type { Event } from '../../interfaces/Calendar.model';

interface CalendarCellProps {
    date: Date;
    isCurrentMonth: boolean;
    events: Event[];
}

export default function CalendarCell({ date, isCurrentMonth, events }: CalendarCellProps) {
    const { t } = useTranslation();
    const isToday = false;

    const EventLink = ({ event }: { event: Event }) => {
        const content = (
            <>
                {event.type.name}
                <br />
                {t('calendar.available', {
                    count: event.availableCapacity ?? 0,
                })}
            </>
        );

        return event.availableCapacity && event.availableCapacity > 0 ? (
            <a href={`/${event.type.uri}/event/${event.id}`}>{content}</a>
        ) : (
            content
        );
    };

    return (
        <td
            className={twMerge(
                'h-28 relative border-grey border ',
                'text-backgroundGreen',
                isCurrentMonth ? 'bg-white day_cell' : 'bg-white out-of-range',
                isToday ? 'today' : ''
            )}
        >
            <div
                className={twMerge(
                    'absolute top-2 right-2 date z-30 text-backgroundGreen',
                    isCurrentMonth ? '' : 'opacity-20',
                    events.length > 0 ? 'has_events' : ''
                )}
            >
                {date.getDate()}
            </div>

            {events.map((event, _index) => {
                const eventEndsTodayInTheMorning =
                    date.toDateString() === event.endDate.toDateString() &&
                    event.endDate.getHours() <= 14;
                const eventStartsTodayInTheAfternoon =
                    date.toDateString() === event.startDate.toDateString() &&
                    event.startDate.getHours() >= 12;

                // In case of a multi-day event and the event started in the afternoon of the previous day
                // Or the event ends in the morning of the next day
                // Or the events starts and ends of today

                const eventStartsToday = date.toDateString() === event.startDate.toDateString();
                const eventEndsToday = date.toDateString() === event.endDate.toDateString();

                // Check if the event started in the afternoon of the previous day and continues to today
                const previousDay = new Date(date);
                previousDay.setDate(date.getDate() - 1);

                const eventStartedYesterdayAfternoon =
                    previousDay.toDateString() === event.startDate.toDateString() &&
                    event.startDate.getHours() >= 12;

                const eventStartedTodayInTheMorning =
                    eventStartsToday && event.startDate.getHours() < 12;

                const firstWholeDayEvent =
                    (eventStartsToday && eventEndsToday) ||
                    eventStartedYesterdayAfternoon ||
                    eventStartedTodayInTheMorning;

                const wholeDayEvent =
                    event.startDate.getTime() < new Date(date).setHours(0, 0) &&
                    event.endDate.getTime() > new Date(date).setHours(15, 0);

                const backgroundColor =
                    event.startDate > new Date()
                        ? `bg-event-${event.type.slug}`
                        : 'bg-event-default';
                const opacity =
                    event.availableCapacity && event.availableCapacity > 0
                        ? 'opacity-100'
                        : 'opacity-50';

                if (firstWholeDayEvent || wholeDayEvent) {
                    return (
                        <div
                            key={event.id}
                            className={twMerge(
                                'inset-0 absolute',
                                backgroundColor,
                                opacity,
                                'flex items-center justify-center text-center',
                                'font-interstate text-xs md:text-sm '
                            )}
                        >
                            {firstWholeDayEvent && <EventLink event={event} />}
                        </div>
                    );
                }
                if (eventStartsTodayInTheAfternoon) {
                    return (
                        <div
                            key={event.id}
                            className={twMerge(
                                'inset-0 w-1/2 left-1/2 absolute',
                                backgroundColor,
                                opacity
                            )}
                        ></div>
                    );
                }
                if (eventEndsTodayInTheMorning) {
                    return (
                        <div
                            key={event.id}
                            className={twMerge(
                                'inset-0 w-1/2 right-1/2 absolute',
                                backgroundColor,
                                opacity
                            )}
                        ></div>
                    );
                }
                return null;
            })}
        </td>
    );
}
